import { Component, computed, inject, input, output } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_FORMFIELD } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { AgenciesClient, AgencyMiniDto } from '@unity/clients';
import { DropdownControlComponent } from '@unity/forms/dropdown-control/dropdown-control.component';

type Filter = 'all' | 'internal' | 'external';

@Component({
  selector: 'unity-dropdown-agency-control',
  standalone: true,
  imports: [KENDO_DROPDOWNS, KENDO_FORMFIELD, KENDO_LABEL, ReactiveFormsModule, DropdownControlComponent],
  templateUrl: './dropdown-agency-control.component.html',
  styleUrl: './dropdown-agency-control.component.scss',
})
export class DropdownAgencyControlComponent {
  private readonly agenciesClient = inject(AgenciesClient);

  private allAgenciesResource = rxResource({
    stream: () => this.agenciesClient.getAllAsMini(),
  });

  public readonly $control = input.required<FormControl<number>>({ alias: 'control' });
  public readonly $displayName = input.required<string>({ alias: 'displayName' });
  public readonly $description = input.required<string>({ alias: 'description' });

  public readonly $defaultItem = input({ name: '', id: 0 }, { alias: 'defaultItem' });
  public readonly $showHint = input(true, { alias: 'showHint' });
  public readonly $filter = input<Filter>('all', { alias: 'filter' });

  public selectionChangeEvent = output<AgencyMiniDto>();

  protected readonly $availableAgencies = computed(() => {
    const agencyFilter = this.$filter();
    if (!this.allAgenciesResource.hasValue()) {
      return [];
    }

    const agencies = this.allAgenciesResource.value();
    return agencies.data.filter(agency =>
      agencyFilter === 'all' ? true : agencyFilter === 'internal' ? agency.is_internal : !agency.is_internal
    );
  });

  protected onSelectionChange(event: unknown) {
    this.selectionChangeEvent.emit(event as AgencyMiniDto);
  }
}
